/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import './index.css';
import { Toaster } from 'react-hot-toast';
import { Analytics } from '@vercel/analytics/react';

// Import i18n configuration
import './i18n/i18n';

// Import components
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import EmailVerifiedRoute from './components/EmailVerifiedRoute';
import DebateFormPage from './pages/DebateFormPage';
import DebateRoomPage from './pages/DebateRoomPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import RulesPage from './pages/RulesPage';
import DebateInvitePage from './pages/DebateInvitePage';
import TeamInvitePage from './pages/TeamInvitePage';
import LandingPage from './pages/LandingPage';
import PublicDebatesPage from './pages/PublicDebatesPage';
import UserDebatesPage from './pages/UserDebatesPage';
import AboutPage from './pages/AboutPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import DebateViewerPage from './pages/DebateViewerPage';
import UserSettingsPage from './pages/UserSettingsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import LinkWhitelistPage from './pages/LinkWhitelistPage';
import CubePage from './pages/CubePage';
import CubeDashboardPage from './pages/CubeDashboardPage';
import MyDashboardPage from './pages/MyDashboardPage';
import UserProfilePage from './pages/UserProfilePage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import UnsubscribePage from './pages/UnsubscribePage';
import PageLayout from './components/PageLayout';

// Import contexts
import { AuthProvider } from './contexts/AuthContext';
import { DebateProvider } from './contexts/DebateContext';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Memoize DebateRoomPage to prevent unnecessary re-renders
const MemoizedDebateRoomPage = React.memo(DebateRoomPage);

function App() {

  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <DebateProvider>
          <Router>
            <div className="app">
              <Toaster position="top-right" />
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Navigate to="/landing" replace />} />
                <Route path="/landing" element={
                  <>
                    <LandingPage />
                    <Footer />
                  </>
                } />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                <Route path="/debates/public" element={
                  <PageLayout>
                    <PublicDebatesPage />
                  </PageLayout>
                } />
                <Route path="/rules" element={
                  <>
                    <Header />
                    <main className="main-content">
                      <RulesPage />
                    </main>
                    <Footer />
                  </>
                } />
                <Route path="/about" element={
                  <>
                    <Header />
                    <main className="main-content">
                      <AboutPage />
                    </main>
                    <Footer />
                  </>
                } />
                <Route path="/terms" element={
                  <>
                    <Header />
                    <main className="main-content">
                      <TermsOfServicePage />
                    </main>
                    <Footer />
                  </>
                } />
                <Route path="/privacy" element={
                  <>
                    <Header />
                    <main className="main-content">
                      <PrivacyPolicyPage />
                    </main>
                    <Footer />
                  </>
                } />
                <Route path="/link-whitelist" element={
                  <>
                    <Header />
                    <main className="main-content">
                      <LinkWhitelistPage />
                    </main>
                    <Footer />
                  </>
                } />
                
                {/* Public debate viewing - no authentication required */}
                <Route path="/debate/view/:id" element={
                  <>
                    <Header />
                    <main className="main-content">
                      <DebateViewerPage />
                    </main>
                    <Footer />
                  </>
                } />
                
                {/* CUBE browsing - no authentication required */}
                <Route path="/cube" element={
                  <PageLayout>
                    <CubePage />
                  </PageLayout>
                } />
                
                {/* User Profile browsing - no authentication required */}
                <Route path="/user/:userId" element={
                  <>
                    <Header />
                    <main className="main-content">
                      <UserProfilePage />
                    </main>
                    <Footer />
                  </>
                } />
                
                {/* Leaderboard browsing - no authentication required */}
                <Route path="/leaderboards" element={
                  <PageLayout>
                    <LeaderboardPage />
                  </PageLayout>
                } />
                
                {/* Protected routes - these need layout components */}
                <Route path="/home" element={
                  <ProtectedRoute>
                    <PageLayout>
                      <UserDebatesPage />
                    </PageLayout>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <PageLayout>
                      <MyDashboardPage />
                    </PageLayout>
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <>
                      <Header />
                      <main className="main-content">
                        <UserSettingsPage />
                      </main>
                      <Footer />
                    </>
                  </ProtectedRoute>
                } />
                <Route path="/cube/dashboard" element={
                  <ProtectedRoute>
                    <>
                      <Header />
                      <main className="main-content">
                        <CubeDashboardPage />
                      </main>
                      <Footer />
                    </>
                  </ProtectedRoute>
                } />
                <Route path="/debates/new" element={
                  <ProtectedRoute>
                    <EmailVerifiedRoute>
                      <PageLayout>
                        <DebateFormPage />
                      </PageLayout>
                    </EmailVerifiedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/debate/:id" element={
                  <ProtectedRoute>
                    <>
                      <Header />
                      <main className="main-content">
                        <MemoizedDebateRoomPage />
                      </main>
                      <Footer />
                    </>
                  </ProtectedRoute>
                } />
                <Route path="/invite/:inviteCode" element={
                  <ProtectedRoute>
                    <>
                      <Header />
                      <main className="main-content">
                        <DebateInvitePage />
                      </main>
                      <Footer />
                    </>
                  </ProtectedRoute>
                } />
                
                {/* New route for team-specific invites */}
                <Route path="/invite/team/:inviteCode" element={
                  <ProtectedRoute>
                    <>
                      <Header />
                      <main className="main-content">
                        <TeamInvitePage />
                      </main>
                      <Footer />
                    </>
                  </ProtectedRoute>
                } />
                
                {/* Email Verification route */}
                <Route path="/email-verification" element={<EmailVerificationPage />} />
                
                {/* Email Verification route (alias for compatibility) */}
                <Route path="/verify-email" element={<EmailVerificationPage />} />
                
                {/* Unsubscribe route */}
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                
                {/* 404 route */}
                <Route path="*" element={
                  <>
                    <Header />
                    <main className="main-content">
                      <NotFoundPage />
                    </main>
                    <Footer />
                  </>
                } />
              </Routes>
              <Analytics />
            </div>
          </Router>
        </DebateProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 